import { WHITE_LABELLING_CONTENT } from "@constants/constants";
import { GET_WHITE_LABELLING_CONTENT, GET_WHITE_LABELLING_CONTENT_BACKEND } from "@constants/urlConstants";
import { getEnvironmentVariablesValue } from "@utility/environmentVariables";
import getParsedConfiguration from "@utility/getParsedConfiguration";
import { isPlainObject, isUndefined } from "@utility/lodash";
import makeRequest from "@utility/makeRequest";
import type { AppContext } from "next/app";

export const computeWhitelabellingContentRequestPayload = (appContext: AppContext): WhiteLabellingContentRequestPayload => {
    const { pathname, query } = appContext?.ctx ?? {};
    // Only dynamic organization pages use this query value, not /login?organizationName=...
    const isOrganizationRoute = pathname?.includes("[organizationName]") || pathname?.includes("[...organizationName]");
    const value = isOrganizationRoute ? query?.organizationName : null;
    const organizationName = (Array.isArray(value) ? value[0] : value) || null;
    return {
        domain: getEnvironmentVariablesValue("ENVIRONMENT_BASE_URL") || "https://rsh.lumenore.com",
        organizationName,
    };
};

/** Stringify the content, replace supplied variables, then parse it back. */
export const resolveWhiteLabellingVariables = <T extends object>(content: T, variables: Record<string, string | number>): T => {
    let contentString = JSON.stringify(content);
    for (const [key, value] of Object.entries(variables)) {
        contentString = contentString.replaceAll(`\${${key}}`, () => JSON.stringify(String(value)).slice(1, -1));
    }
    return getParsedConfiguration(contentString);
};

export const getResolvedWhitelabellingContent = (rawWhitelabellingContent: unknown = {}): WhiteLabellingContent => {
    const resolutionVariables = {
        year: new Date().getFullYear(),
    };
    const merge = (defaultValue: unknown, rawValue: unknown): unknown => {
        if (isUndefined(rawValue)) return defaultValue;
        if (isPlainObject(defaultValue) && isPlainObject(rawValue)) return Object.fromEntries(Object.entries(defaultValue).map(([key, value]) => [
            key, merge(value, Object.hasOwn(rawValue, key) ? rawValue[key] : undefined),
        ]));
        return rawValue;
    };
    return resolveWhiteLabellingVariables(merge(WHITE_LABELLING_CONTENT, isPlainObject(rawWhitelabellingContent) ? rawWhitelabellingContent : undefined) as WhiteLabellingContent, resolutionVariables);
};

/** Fetch the content, then fill missing values using the existing recursive resolver. */
export const fetchWhiteLabellingContent = async (whitelabellingContentRequestPayload: WhiteLabellingContentRequestPayload): Promise<WhiteLabellingContent> => {
    try {
        const requestUrl = isUndefined(globalThis.window) ? GET_WHITE_LABELLING_CONTENT_BACKEND : GET_WHITE_LABELLING_CONTENT;
        const requestData = { data: { ...whitelabellingContentRequestPayload } };
        const response = await makeRequest<APIResponse<string | Record<string, unknown>>>(requestUrl, requestData, "POST");
        if (String(response?.status?.code) === "200" && response?.data) {
            const whitelabellingContent = getResolvedWhitelabellingContent(typeof response.data === "string" ? getParsedConfiguration(response.data) : response.data);
            return whitelabellingContent;
        }
        throw new Error("WhiteLabelling API returned an unsuccessful status or missing data");
    } catch {
        const fallbackContent = getResolvedWhitelabellingContent();
        console.log("[WhiteLabelling] Failed to load whitelabelling content. Using default content:", fallbackContent);
        return fallbackContent;
    }
};
